import { inject, observer } from "mobx-react";
import React, { FC } from "react";
import {
  CommonsStore,
  GoBackButton,
  IndexView,
  SecurityProvider,
  withBreadCrumbs,
  SecuredIconButton,
  I18n,
  RemoteDataSource,
  ShowView
} from "lego-gui-lib";
import { t } from "../../../core/i18n/i18n";
import { color } from "../../../core/theme";
import { CalendarDayTypeSchemaColumns } from "./schema";
import DeleteIcon from "@material-ui/icons/DeleteOutlined";
import { DefaultTitleContainer } from "lego-gui-lib/lib/components/Views/OneView";
import { TitleRow } from "lego-gui-lib/lib/components/Views/TitleRow";
import { NoTitleContainer } from "../../../common/tabContainers";
import { SimpleCreateField } from "./SimpleCreateField";
import {
  Card,
  CardContent,
  createStyles,
  darken,
  lighten,
  Theme,
  Tooltip,
  WithStyles,
  withStyles
} from "@material-ui/core";
import { RadioButtonsGroup } from "./radioButtonsGroup";
import { Calendar, withMultipleDates } from "react-infinite-calendar";
import "react-infinite-calendar/styles.css";
import {
  CalendarDto,
  createCalendarDto,
  createColorsMap,
  createPopupMessage,
  createSelectedArray,
  getAddFieldPlaceholder,
  getCalendarTitle
} from "./CalendarMappers";
import { deleteButtonWithReload } from "./deleteButtonWithReload";
import {
  DataTableRow,
  DataTableRowProps
} from "lego-gui-lib/lib/components/DataTable/DataTableRow";
import { stringToColour } from "lego-gui-lib/lib/helpers/stringToColour";
import { EditCalendarSchema } from "../schema";
import { useHistory } from "react-router";

const calendarStyles = (theme: Theme) =>
  createStyles({
    gridContainer: {
      display: "grid",
      gridTemplateColumns: "35% 65%",
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "100%"
      }
    },
    list: {
      paddingTop: "72px"
    },
    calendar: {
      gridArea: "1 / 2 / 4 / 3",
      marginLeft: "3%",
      [theme.breakpoints.down("md")]: {
        gridArea: "3 / 1 / 4 / 1",
        marginLeft: "inherit"
      }
    },
    radioButtons: {
      marginTop: "20px"
    },
    card: {
      display: "grid",
      gridTemplateColumns: "100%"
    }
  });

export interface CalendarProps extends WithStyles<typeof calendarStyles> {
  calendarDayType: any;
  calendar: RemoteDataSource<any, any>;
  commons: CommonsStore;
  auth: any;
  match: any;
  goBack: () => void;

  [otherProp: string]: any;
}

export interface CalendarState {
  selected: Date[];
  // day , color
  colorsMap: Map<string, string>;
  method: string;
  lastSelectedDay: Date;
  disabled: boolean;
}

export const withSingleSelectable = (Component: DataTableRowProps & any) => (
  props: any
) => {
  return <Component multiSelect={false} selectableRows={true} {...props} />;
};

const CalendarWithMultipleDates = withMultipleDates(Calendar);

@inject("calendarDayType", "calendar", "commons", "auth", "i18n")
@observer
export class ShowCalendarComponent extends React.Component<
  CalendarProps,
  CalendarState
> {
  private today: Date;

  public constructor(props: CalendarProps) {
    super(props);
    this.state = this.createInitialState();
    this.today = new Date();
    this.today.setHours(0, 0, 0, 0);
  }

  private createInitialState() {
    const resultMap: Map<string, string[]> = new Map([]);
    const selected = createSelectedArray(resultMap);
    const colorsMap = createColorsMap(resultMap);
    const lastSelectedDay = new Date();
    const disabled = false;
    const method = "DAY";
    return {
      selected,
      colorsMap,
      method,
      lastSelectedDay,
      disabled
    };
  }

  public componentDidMount(): void {
    this.loadData();
  }

  private loadData = () => {
    this.props.calendarDayType.listDataSource
      .getTypeNamesWithDays()
      .then((resultMap: Map<string, string[]>) => {
        this.setState({
          colorsMap: createColorsMap(resultMap),
          selected: createSelectedArray(resultMap)
        });
      });

    this.props.calendar.oneDataSource
      .findOne(this.props.match.params)
      .then((result: any) => {
        this.setState({
          disabled: !this.props.auth.isAdmin() && result.visibility === 0
        });
      });
  };

  private handleMethodChange = (value: string) => {
    this.setState({ method: value });
  };

  private calendarMarkEvent = (date: Date) => {
    if (
      this.props.calendarDayType.listDataSource.selected.length != 0 &&
      !this.state.disabled &&
      this.today.getTime() <= date.getTime()
    ) {
      const dayType = this.props.calendarDayType.listDataSource.selected[0]
        .name;
      this.setState({ lastSelectedDay: date });
      this.createMarkingDto(date, dayType);
    }

    return undefined;
  };

  private createMarkingDto = (date: any, dayType: string) => {
    const selectedMap = this.state.selected.map(date =>
      date.toLocaleDateString("en-GB")
    );
    const index = selectedMap.indexOf(date.toLocaleDateString("en-GB"));
    let isClearing = true;

    if (index === -1) {
      isClearing = false;
    }

    const calendarDto = createCalendarDto(date, this.state.method, isClearing);

    this.props.commons.showDialog(
      createPopupMessage(date, dayType, isClearing, this.state.method),
      this.executeMarking(calendarDto)
    );
  };

  private executeMarking = (calendarDto: CalendarDto) => () => {
    const dayType = this.props.calendarDayType.listDataSource.selected[0];
    this.props.calendarDayType.listDataSource
      .assignTypes(dayType, calendarDto)
      .then((resultMap: Map<string, string[]>) => {
        this.setState({
          colorsMap: createColorsMap(resultMap),
          selected: createSelectedArray(resultMap)
        });
      });
    this.props.commons.closeDialog();
  };

  private getTypeColor = () => {
    if (this.props.calendarDayType.listDataSource.selected.length != 0) {
      const typeName = this.props.calendarDayType.listDataSource.selected[0]
        .name;
      return stringToColour(typeName);
    } else {
      return "rgb(255,148,231)";
    }
  };

  private pickColor = (date: string) => {
    return this.state.colorsMap.get(date) || this.getTypeColor();
  };

  public render() {
    const { calendarDayType, classes, match } = this.props;
    const rest = this.props as any;
    const i18n = this.props.i18n as I18n;

    return withBreadCrumbs(
      { to: match.url + "/edit", children: getCalendarTitle() },
      <SecurityProvider security={{}}>
        <DefaultTitleContainer>
          <TitleRow
            prefix={<GoBackButton goBack={this.props.goBack} />}
            suffix={this.deleteButton()}
            title={getCalendarTitle()}
          />
        </DefaultTitleContainer>
        <Card variant="outlined" className={classes.card}>
          <CardContent>
            <div className={classes.gridContainer}>
              <ShowView
                contentContainer={false}
                viewContainer={false}
                titleRow={false}
                id={this.props.match.params}
                onlyView={this.state.disabled}
                dataSource={this.props.calendar.oneDataSource}
                fields={EditCalendarSchema}
              />
              <div className={classes.list}>
                {this.state.disabled ? (
                  <></>
                ) : (
                  <SimpleCreateField
                    dataSource={calendarDayType.oneDataSource}
                    placeholder={getAddFieldPlaceholder()}
                    fieldName={"name"}
                    minNumberOfLetters={4}
                  />
                )}
                <IndexView
                  baseUrl={"/dayTypes"}
                  dataSource={calendarDayType.listDataSource}
                  disableCheckboxes={true}
                  inlineRowButtons={true}
                  rowButtons={
                    this.state.disabled
                      ? []
                      : deleteButtonWithReload(this.loadData)
                  }
                  {...rest}
                  title={""}
                  pageSizeOptions={[10]}
                  tableRowComponent={
                    this.state.disabled
                      ? undefined
                      : withSingleSelectable(DataTableRow)
                  }
                  columns={CalendarDayTypeSchemaColumns}
                  titleContainer={NoTitleContainer}
                  // prevents classes from {...rest} props to trigger errors on type mismatch as calendarStyles are not compatible with IndexView styles
                  classes={undefined}
                />
                {this.state.disabled ? (
                  <></>
                ) : (
                  <div className={classes.radioButtons}>
                    <RadioButtonsGroup
                      callback={this.handleMethodChange}
                      disabled={
                        calendarDayType.listDataSource.selected.length == 0
                      }
                    />
                  </div>
                )}
              </div>
              <div className={classes.calendar}>
                <CalendarWithMultipleDates
                  onSelect={this.calendarMarkEvent}
                  selected={this.state.selected as any}
                  width={"100%"}
                  height={700}
                  min={new Date(2019, 12, 31)}
                  max={new Date(2039, 11, 30)}
                  locale={{
                    weekStartsOn: 1,
                    weekdays: [
                      i18n.t("hns:calendar:weekDays:sunday"),
                      i18n.t("hns:calendar:weekDays:monday"),
                      i18n.t("hns:calendar:weekDays:tuesday"),
                      i18n.t("hns:calendar:weekDays:wednesday"),
                      i18n.t("hns:calendar:weekDays:thursday"),
                      i18n.t("hns:calendar:weekDays:friday"),
                      i18n.t("hns:calendar:weekDays:saturday")
                    ],
                    blank: i18n.t("hns:calendar:blank"),
                    todayLabel: {
                      long: i18n.t("hns:calendar:todayLabel")
                    }
                  }}
                  theme={{
                    selectionColor: this.pickColor as any,
                    textColor: {
                      default: "#333",
                      active: "#FFF"
                    },
                    weekdayColor: lighten(color.magenta, 0.1),
                    headerColor: color.magenta,
                    floatingNav: {
                      background: darken(color.magenta, 0.1),
                      color: "#FFF",
                      chevron: "#FFA726"
                    }
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </SecurityProvider>
    );
  }

  private deleteButton = () => {
    const title = t("lego:views.index.row.delete", "Delete")();
    return (
      this.state.disabled && (
        <Tooltip title={title}>
          <SecuredIconButton
            requiredAccess={this.props.required}
            onClick={this.openDeleteDialog}
            style={{ float: "right" }}
            color="primary"
          >
            <DeleteIcon />
          </SecuredIconButton>
        </Tooltip>
      )
    );
  };

  private openDeleteDialog = () => {
    const i18n = this.props?.i18n as I18n;

    this.props.commons.showDialog(
      {
        title: i18n.t("lego:views.show.confirmDelete.title"),
        content: i18n.t("lego:views.show.confirmDelete.content")
      },
      () => {
        this.props.calendar.oneDataSource
          .delete()
          .then(() => this.props.goBack())
          .finally(this.props.commons.closeDialog);
      }
    );
  };
}

const ConfiguredCalendar: FC<CalendarProps> = props => {
  const history = useHistory();
  return <ShowCalendarComponent {...props} goBack={() => history.goBack()} />;
};

export const ShowCalendar = withStyles(calendarStyles, { withTheme: true })(
  ConfiguredCalendar
);
